import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFetchData } from "app/hooks/hooks";
import { dashboardTestsperformedGet } from "app/api/v2/dashboardApi";
import { ChartColorsArray } from "app/constants/dashboardConst";
import _cloneDeep from "lodash/cloneDeep";
import {
  formatChartDate,
  formatSiteFilterData,
} from "app/helpers/dashboardHelper";
import DashboardItemLayout, {
  DashboardFilterValue,
} from "../DashboardItemLayout/DashboardItemLayout";
import { ChartData, ChartDataSets } from "../DashboardChart/DashboardChart";
import {
  UserType,
  DashboardTestFlowsPerformed,
  DashboardApiV2DashboardTestFlowsPerformedGetRequest,
  DashboardFlowSite,
} from "@generated/v2";
import { DashboardItemFilterBase } from "app/types/dashboardType";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { SwitchProps } from "antd/lib/switch";
import { ChartOptions } from "chart.js";
import { LineChartWithCustomLegend } from "app/screens/DashboardScreen/components/DashboardPremium/components/DashboardChart/LineChartWithCustomLegend";
import { DashboardItemFilterProps } from "app/screens/DashboardScreen/components/DashboardPremium/components/DashboardItemLayout/components/DashboardItemFilter/DashboardItemFilter";

const LIMIT = 3;

const ChartNumberOfTests = ({
  customerId,
  start,
  end,
  period,
  userType,
}: DashboardItemFilterBase) => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<DashboardFilterValue>([]);
  const [showData, setShowData] = useState<Array<boolean>>(
    new Array(LIMIT).fill(true)
  );

  // When switching customers, reset checkboxes
  useEffect(() => {
    setShowData(new Array(LIMIT).fill(true));
  }, [customerId]);

  const { data, loading } = useFetchData<
    DashboardTestFlowsPerformed,
    DashboardApiV2DashboardTestFlowsPerformedGetRequest
  >(dashboardTestsperformedGet, {
    shouldCallApi: !!customerId,
    params: {
      customerId,
      start,
      end,
    },
  });

  // Functions

  const toggleShowData = useCallback(
    (index: number) => {
      const newArr = [...showData];
      newArr[index] = !newArr[index];
      setShowData(newArr);
    },
    [showData]
  );

  // Memoized values

  const hasFilters = useMemo(
    () =>
      ((userType !== UserType.SiteAdmin && data?.sites?.length) ?? 0) > LIMIT,
    [data, userType]
  );

  const filters = useMemo<DashboardItemFilterProps>(
    () => ({
      data: formatSiteFilterData(data?.sites),
      label: t("dashboard.siteChartFilterLabel"),
      limit: LIMIT,
      onChange: setSelected,
    }),
    [data, t]
  );

  const switches: SwitchProps[] = useMemo(
    () =>
      !hasFilters
        ? showData
            // map through array of flags to show the data and create switches
            .map((item, index) => ({
              checked: item,
              onChange: () => toggleShowData(index),
              title: `${data?.sites?.[index]?.siteName}`,
            }))
            // slice array to get as many switches as many sites for the customer
            .slice(0, data?.sites?.length)
        : [],
    [data, hasFilters, showData, toggleShowData]
  );

  const chartData = useMemo<ChartData>(() => {
    if (!data || !selected.length) return;

    const dataClone = _cloneDeep(data);

    let sites: DashboardFlowSite[] = [];

    for (let i = 0; i < selected.length; i++) {
      const site = dataClone?.sites?.find(
        site => site.siteId === (selected[i] as number)
      );

      if (!!site) {
        sites.push(site);
      }
    }

    const datasets: ChartDataSets[] = sites.map((site, index) => {
      return {
        label: hasFilters
          ? `${t("dashboard.siteChartFilterLabel")} ${index + 1}`
          : site.siteName ?? undefined,
        data: site.testFlowCount ?? undefined,
        hidden: !showData?.[index],
        backgroundColor: "gba(0, 0, 0, 0)",
        borderColor: ChartColorsArray[index],
        fill: false,
        borderDash: index === 0 ? [1, 6] : index === 1 ? [6, 6] : undefined,
        borderCapStyle: "round",
        borderDashOffset: 1,
      };
    });

    const labels = !!dataClone?.dates
      ? formatChartDate(dataClone?.dates, dataClone?.type)
      : [];
    return { datasets, labels };
  }, [data, hasFilters, selected, showData, t]);

  const options = useMemo<ChartOptions>(() => {
    const op: ChartOptions = {
      elements: {
        line: {
          cubicInterpolationMode: "monotone",
        },
      },
    };
    return op;
  }, []);

  return (
    <ErrorFallbackUI>
      <DashboardItemLayout
        background
        title={t("dashboard.numberOfTestFlowsTitle")}
        subTitle={period}
        info={t("dashboard.numberOfTestFlowsInfo")}
        filters={filters}
        loading={loading}
        switches={switches}
      >
        <LineChartWithCustomLegend
          type="line"
          data={chartData}
          options={options}
          toggleShowData={toggleShowData}
        />
      </DashboardItemLayout>
    </ErrorFallbackUI>
  );
};

export default memo(ChartNumberOfTests);
